import React, { createContext, useCallback, useMemo, useRef, useState } from 'react'
import { useOverlay } from '@fs/zion-ui'
import uuid from '../utils/uuid'
import useSessionStats from '../hooks/useSessionStats'
import useCache from '../hooks/useCache'
import { submitBatch as mockSubmitBatch } from '../pages/fix-bad-characters/mockService'

export interface NameFields {
  namePrefix: string
  nameGiven: string
  nameFamily: string
  nameSuffix: string
  reason: string
}

export interface StepData extends NameFields {
  id: string
  dpid: string
  selectedType?: string
  savedData?: NameFields | null
}

export interface StepPosition {
  x: number
  y: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OverlayType = ReturnType<typeof useOverlay>

export interface FixBadCharactersContextType {
  backHandler: () => void
  clearCache: () => Promise<void>
  congratulationsDialogOverlay: OverlayType
  editedFields: NameFields
  getCacheItem: () => Promise<any> // eslint-disable-line @typescript-eslint/no-explicit-any
  nextHandler: () => void
  previousHandler: () => void
  saveBatch: () => Promise<void>
  scrollToPerson: React.MutableRefObject<() => void>
  setCacheItem: (value: any) => Promise<void> // eslint-disable-line @typescript-eslint/no-explicit-any
  setCheckoutData: (data: Partial<StepData>[]) => Promise<void>
  setEditedFields: React.Dispatch<React.SetStateAction<NameFields>>
  setStep: React.Dispatch<React.SetStateAction<StepPosition>>
  setSteps: React.Dispatch<React.SetStateAction<StepData[]>>
  step: StepPosition
  steps: StepData[]
  updateStepData: (params: { id: string; selectedType?: string; savedData?: NameFields | null }) => void
}

export const FixBadCharactersContext = createContext<FixBadCharactersContextType>({} as FixBadCharactersContextType)

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = (): void => {}

const emptyFields: NameFields = { namePrefix: '', nameGiven: '', nameFamily: '', nameSuffix: '', reason: '' }

export function fieldsFromStep(s: StepData | NameFields | null | undefined): NameFields {
  if (!s) return emptyFields
  const source = (s as StepData).savedData || s
  return {
    namePrefix: source.namePrefix || '',
    nameGiven: source.nameGiven || '',
    nameFamily: source.nameFamily || '',
    nameSuffix: source.nameSuffix || '',
    reason: source.reason || '',
  }
}

export const FixBadCharactersProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [step, setStep] = useState<StepPosition>({ x: 1, y: 10 })
  const [steps, setSteps] = useState<StepData[]>([])
  const [editedFields, setEditedFields] = useState<NameFields>(emptyFields)
  const scrollToPerson = useRef<() => void>(noop)
  const congratulationsDialogOverlay = useOverlay()
  const sessionStorageKey = 'fixBadCharactersSession'
  const { updateStats } = useSessionStats()
  const { getItem, removeItem, setItem } = useCache(sessionStorageKey)

  const setCheckoutData = useCallback(
    async (data: Partial<StepData>[]) => {
      if (!data?.length) {
        setStep({ x: 1, y: 0 })
        setSteps([])
        return
      }

      const batch: StepData[] = data.map((record) => ({
        id: record.id || uuid(),
        dpid: record.dpid || '',
        namePrefix: record.namePrefix || '',
        nameGiven: record.nameGiven || '',
        nameFamily: record.nameFamily || '',
        nameSuffix: record.nameSuffix || '',
        reason: record.reason || '',
      }))
      await setItem({ batch, currentStep: 0 })
      setSteps(batch)
      setStep({ x: 1, y: batch.length })
      setEditedFields(fieldsFromStep(batch[0]))
    },
    [setItem]
  )

  const saveBatch = useCallback(async () => {
    try {
      const cached = await getItem()
      const batch = cached?.batch
      if (batch?.length) {
        await mockSubmitBatch(batch)
        const correctedCount = batch.filter((item: StepData) => item.selectedType === 'SUBMITTED').length
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        updateStats((currentStats: any) => ({
          ...currentStats,
          fixBadCharacters: {
            prev: currentStats?.fixBadCharacters?.prev || 0,
            cur: (currentStats?.fixBadCharacters?.cur || 0) + correctedCount,
          },
        }))
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('FixBadCharactersProvider::saveBatch error', err)
    } finally {
      await removeItem()
    }
  }, [removeItem, getItem, updateStats])

  const previousHandler = useCallback(() => {
    setSteps((currentSteps) => {
      setStep((cur) => {
        if (cur.x > 1) {
          const prev = { ...cur, x: cur.x - 1 }
          setEditedFields(fieldsFromStep(currentSteps[prev.x - 1]))
          scrollToPerson.current()
          return prev
        }
        return cur
      })
      return currentSteps
    })
  }, [])

  const nextHandler = useCallback(() => {
    setSteps((currentSteps) => {
      setStep((cur) => {
        if (cur.x < cur.y) {
          const next = { ...cur, x: cur.x + 1 }
          setEditedFields(fieldsFromStep(currentSteps[next.x - 1]))
          scrollToPerson.current()
          return next
        }
        congratulationsDialogOverlay.open()
        return cur
      })
      return currentSteps
    })
  }, [congratulationsDialogOverlay])

  const backHandler = useCallback(() => {
    saveBatch()
    congratulationsDialogOverlay.close()
    setSteps([])
    setStep({ x: 1, y: 0 })
    setEditedFields({ namePrefix: '', nameGiven: '', nameFamily: '', nameSuffix: '', reason: '' })
  }, [saveBatch, congratulationsDialogOverlay])

  const updateStepData = useCallback(
    ({ id, selectedType, savedData }: { id: string; selectedType?: string; savedData?: NameFields | null }) => {
      setSteps((prevBatch) => {
        const batch = prevBatch.map((stp) => ({
          ...stp,
          ...(stp.id === id && { selectedType, savedData }),
        }))
        const currentStepIndex = batch.findIndex((sp) => sp.id === id)
        setItem({ batch, currentStep: currentStepIndex + 1 })
        return batch
      })
    },
    [setItem]
  )

  const value = useMemo(
    () => ({
      backHandler,
      clearCache: removeItem,
      congratulationsDialogOverlay,
      editedFields,
      getCacheItem: getItem,
      nextHandler,
      previousHandler,
      saveBatch,
      scrollToPerson,
      setCacheItem: setItem,
      setCheckoutData,
      setEditedFields,
      setStep,
      setSteps,
      step,
      steps,
      updateStepData,
    }),
    [
      backHandler,
      removeItem,
      congratulationsDialogOverlay,
      editedFields,
      getItem,
      nextHandler,
      previousHandler,
      saveBatch,
      setCheckoutData,
      setItem,
      step,
      steps,
      updateStepData,
    ]
  )

  return <FixBadCharactersContext.Provider value={value}>{children}</FixBadCharactersContext.Provider>
}
